import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Turnstile, { useTurnstile } from 'react-turnstile';
import 'url-search-params-polyfill';
import { PromiseType } from 'utility-types';
import { aliasUser, identifyUser, trackSignUpCompleted, trackSignUpStarted } from '../../lib/analytics';
import { getAttributionData, getMarketingAnonId } from '../../lib/attribution';
import Auth from '../../lib/Auth';
import { TURNSTILE_SITE_KEY } from '../../lib/constants';
import { useTheme } from '../../theme/useTheme';
import ActivatePlanNotice from '../ActivatePlan/ActivatePlanNotice';
import { getPlanActivationEmail } from '../ActivatePlan/helpers';
import Button from '../Button';
import Card from '../Card';
import { FormStatus } from '../FormStatus';
import GoogleSignIn from '../GoogleSignIn';
import Input from '../Input';
import PageSeo, { meta } from '../PageSeo';
import PortalLayout from '../PortalLayout';
import TextLink from '../TextLink';
import { signup } from './mutations';
import { showSignupRedirectModal } from './SignupRedirectModal';
import { getApolloErrorMessageCodes } from '../../lib/errors';

declare global {
  interface Window {
    Intercom: Intercom_.IntercomStatic;
  }
}

type LocationProps = {
  email?: string;
};

type FormProps = {
  email: string;
  password: string;
  captchaResponse: string;
};

const getEmail = (queryEmail: string | null, state: LocationProps) => {
  if (state?.email) {
    return state?.email;
  }

  if (queryEmail && queryEmail !== 'null') {
    return decodeURIComponent(queryEmail);
  }

  return '';
};

export default function Signup() {
  const location = useLocation<LocationProps>();
  const [query] = useState(new URLSearchParams(location.search));
  const [registerError, setRegisterError] = useState<string>();
  const emailFromQuery = getEmail(query.get('email'), location.state);
  const planActivationEmail = getPlanActivationEmail();
  const [email, setEmail] = useState(emailFromQuery || planActivationEmail || '');
  const [password, setPassword] = useState<string>('');
  const [isClean, setIsClean] = useState(true);
  const [captchaResponse, setCaptchaResponse] = useState<string | null>(null);
  const turnstile = useTurnstile();

  const { theme } = useTheme();

  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async ({ email, password, captchaResponse }: FormProps) => {
      setIsSubmitting(true);

      let response: PromiseType<ReturnType<typeof signup>>;

      try {
        response = await signup({ email, password, captchaResponse });
      } finally {
        setIsSubmitting(false);
      }

      const { id, userHash } = response;

      Auth.saveTokenData();

      // Set marketing anonymous_id so RudderStack can link pre-signup pageviews
      const marketingAnonId = getMarketingAnonId();
      if (marketingAnonId && window.rudderanalytics?.setAnonymousId) {
        window.rudderanalytics.setAnonymousId(marketingAnonId);
      }

      aliasUser(String(id), email);
      identifyUser(String(id), { email, ...getAttributionData() }, { intercomUserHash: userHash });

      trackSignUpCompleted(
        {
          location: 'sign_up',
          onboarding_bucket: 'default',
          checkout_source: 'default',
        },
        email,
      );

      history.push('/purchase');
    },
    [history],
  );

  const onChange = useCallback(() => {
    setRegisterError(undefined);
    if (isClean) {
      setIsClean(false);
      trackSignUpStarted('sign_up');
    }
  }, [isClean]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!captchaResponse) throw new Error('Invalid captcha response');

      try {
        await onSubmit({ email, password, captchaResponse });
      } catch (e) {
        const errorCodes = getApolloErrorMessageCodes(e);
        if (errorCodes?.includes('USER_NOT_APPROVED')) {
          showSignupRedirectModal();
          return;
        }
        turnstile.reset();
        setRegisterError(e instanceof Error ? e.message : 'Unable to create a user with those credentials');
      }
    },
    [email, password, onSubmit, captchaResponse, turnstile],
  );

  return (
    <PortalLayout>
      <div className="mx-auto mt-10 px-4 rounded text-center">
        <PageSeo title={meta.SIGNUP.title} description={meta.SIGNUP.description} />
        <Card className="py-4 px-6 max-w-lg mx-auto">
          <div className="my-5 mb-8">
            <h1 className="text-3xl">Create an account</h1>
          </div>
          {registerError && <FormStatus type="error">{registerError}</FormStatus>}
          <ActivatePlanNotice />
          <form data-cy="signup-form" onSubmit={(event) => handleSubmit(event)}>
            <Input
              defaultValue={email}
              className="mb-4"
              placeholder="Email"
              onChange={(event) => {
                onChange();
                setEmail(event.target.value.toLowerCase());
              }}
              name="email"
            />
            <Input
              className="mb-4"
              type="password"
              onChange={(event) => {
                onChange();
                setPassword(event.target.value);
              }}
              name="password"
              placeholder="Password"
            />

            <Turnstile
              sitekey={TURNSTILE_SITE_KEY}
              size="flexible"
              theme={theme}
              onVerify={(token) => {
                setCaptchaResponse(token);
              }}
            />

            <Button
              size="lg"
              fullWidth
              type="submit"
              loading={isSubmitting}
              className="mt-3 mb-4"
              disabled={!captchaResponse}
            >
              Sign up
            </Button>
          </form>
          <div className="mb-4">
            Already have an account? <TextLink to="/login"> Sign in</TextLink>
          </div>
        </Card>
        <GoogleSignIn type="signup" showError={(error) => setRegisterError(error ?? undefined)} />
      </div>
    </PortalLayout>
  );
}
